import React from "react";
import {
  Receipt as ReceiptModel,
  ReceiptNF,
} from "../../../../domain/models/receipt-model";
import { ReadReceiptNF } from "../../../../domain/usecases/read-receipt-nf";
import { SaveReceipt } from "../../../../domain/usecases/save-receipt";
import FileUploader from "../components/drag-and-drop";
import { Paper, Stack, TextField, Typography, Fab } from "@mui/material";
import { Container } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/pt-br";
import dayjs from "dayjs";
import { Dayjs } from "dayjs";
import NewReceiptsTable from "./NewReceiptsTable/NewReceitpsTable";
import { useDispatch, useSelector } from "react-redux";
import { receiptsActions, ReceiptState } from "../store";
import { LoadProductListByManyCodes } from "../../../../domain/usecases/load-product-list-by-many-codes";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { CustomDialog } from "../../../components/Dialog";
import { DuplicatedNfError } from "../../../../domain/errors/duplicated-nf-error";
import { useErrorHandler } from "../../hooks";
import { RootState } from "../../../store";

type Props = {
  readReceiptNF: ReadReceiptNF;
  saveReceipt: SaveReceipt;
  loadProductList: LoadProductListByManyCodes;
};

export const NewReceipt: React.FC<Props> = ({
  readReceiptNF,
  saveReceipt,
  loadProductList,
}) => {
  const [name, setName] = React.useState<string>("");
  const [files, setFiles] = React.useState<FileList | null>(null);
  const [date, setDate] = React.useState<Dayjs | null>(dayjs(Date.now()));
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const dispatch = useDispatch();

  const handleError = useErrorHandler((error) => {
    if (error instanceof DuplicatedNfError) {
      alert(error.message);
    } else {
      alert("Um erro inexperado aconteceu, tente novamenta mais tarde;");
    }
  });

  const nfs: ReceiptNF[] = useSelector<ReceiptState, ReceiptNF[]>(
    (state) => state.data
  );

  const reprocessNfs = async () => {
    try {
      setIsLoading(true);
      const codes: string[] = [];

      nfs.forEach((nf) => {
        nf.items.forEach((item) => {
          codes.push(item.code);
        });
      });

      const productList = await loadProductList.load(codes);

      const old: ReceiptNF[] = JSON.parse(JSON.stringify(nfs));

      const newNfs = old.map((nf) => {
        nf.items.forEach((item) => {
          const product = productList.find(
            (product) => product.eanCode === item.code
          );
          if (!product) {
            item.registered = false;
          } else {
            item.registered = true;
            item.batchControl = !!product.batchControl;
            item.insertBatchLater = false;
            if (product.conversionFactor) {
              item.amount = item.amount * product.conversionFactor;
            }
            if (product.conversionUnit) {
              item.unit = product.conversionUnit;
            }
          }
        });
        return nf;
      });

      dispatch(receiptsActions.updateNfs({ nfs: newNfs }));
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileRead = async (event: ProgressEvent<FileReader>) => {
    try {
      setIsLoading(true);
      const xmlString = event.target?.result as string;
      const nf = readReceiptNF.read(xmlString);
      if (nf) {
        const exists = nfs.find((item) => item.reference === nf.reference);
        if (exists) {
          return alert("Essa nota fiscal já foi inserida nesse recebimento");
        }

        const codes: string[] = [];

        nf.items.map((item) => {
          codes.push(item.code);
        });

        const productList = await loadProductList.load(codes);

        nf.items.map((item) => {
          const product = productList.find(
            (product) => product.eanCode === item.code
          );
          if (!product) return;
          item.registered = true;
          item.batchControl = !!product.batchControl;
          item.insertBatchLater = false;
          if (product.conversionFactor) {
            item.amount = item.amount * product.conversionFactor;
          }
          if (product.conversionUnit) {
            item.unit = product.conversionUnit;
          }
        });

        dispatch(receiptsActions.saveNf({ nf }));
      }
    } catch (error) {
      setIsDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (files: FileList) => {
    setFiles(files);
    const fileList = Array.from(files);
    fileList.forEach((file) => {
      if (file) {
        const reader = new FileReader();
        reader.readAsText(file);
        reader.onload = handleFileRead;
      }
    });
  };

  const emptyBatches = () => {
    let empty = false;
    nfs.map((nf) => {
      nf.items.map((item) => {
        if (item.batchControl && !item.batch) {
          if (!item.insertBatchLater) empty = true;
        }
      });
    });
    return empty;
  };
  const productsWithError = () => {
    let error: string[] = [];
    nfs.map((nf) => {
      nf.items.map((item) => {
        if (!item.registered) error.push(item.name);
      });
    });
    return error;
  };

  const handleButtonClick = async () => {
    setIsLoading(true);
    const warehouseString = localStorage.getItem("currentWarehouse");
    const warehouse = warehouseString
      ? (JSON.parse(warehouseString) as {
          id: number;
          name: string;
        } | null)
      : undefined;

    try {
      if (!name) return alert("Preencha a identificação do recebimento");
      if (!date) return alert("Selecione uma data para o recebimento");
      if (nfs.length === 0) return alert("Insira ao menos uma nota fiscal");

      const error = productsWithError();
      if (error.length > 0)
        return alert(`Os produtos ${error.join(", ")} não estão cadastrados`);
      if (emptyBatches()) return alert("Ainda há lotes não preenchidos");
      const receipt: ReceiptModel = {
        name,
        warehouseId: warehouse!.id,
        date: date.toDate().toISOString(),
        nfs,
      };
      const result = await saveReceipt.save(receipt!);
      if (result) {
        setName("");
        dispatch(receiptsActions.reset());
        alert("Conferência gerada com sucesso");
      }
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Container>
      <Stack sx={{ my: "40px" }} spacing={4}>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <CustomDialog
          isOpen={isDialogOpen}
          title={"Erro ao buscar produtos no Banco de Dados"}
          content={"Por favor tente novamente"}
          onYes={() => {
            setIsDialogOpen(false);
          }}
          handleClose={() => {
            setIsDialogOpen(false);
          }}
        />
        <Container>
          <Paper sx={{ padding: "30px" }}>
            <Stack spacing={1}>
              <Typography>Informações Básicas</Typography>
              <Stack
                direction="row"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <TextField
                  id="outlined-basic"
                  label="Identificação do Recebimento"
                  variant="outlined"
                  size="small"
                  sx={{ width: "400px" }}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                />

                {/* <FormControl sx={{ width: "200px" }} size="small">
                  <InputLabel id="demo-simple-select-label">
                    Conferente
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    // value={age}
                    label="Conferente"
                    // onChange={handleChange}
                  >
                    <MenuItem value={1}>Automático</MenuItem>
                    <MenuItem value={2}>Maurício</MenuItem>
                    <MenuItem value={3}>Outro</MenuItem>
                  </Select>
                </FormControl> */}

                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="pt-br"
                >
                  <DatePicker
                    slotProps={{ textField: { size: "small" } }}
                    value={date}
                    onChange={(newValue) => {
                      setDate(newValue);
                    }}
                  />
                </LocalizationProvider>
              </Stack>
            </Stack>
          </Paper>
        </Container>
        <Container>
          <FileUploader onFilesSelected={handleFileUpload} />
          <Typography>É aceito apenas o formato XML</Typography>
        </Container>
        <Container>
          <NewReceiptsTable />
        </Container>
        <Container sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Fab
            variant="extended"
            color="secondary"
            onClick={reprocessNfs}
            sx={{ mx: "20px" }}
          >
            Processar novamente
          </Fab>
          <Fab variant="extended" color="primary" onClick={handleButtonClick}>
            Gerar Conferência
          </Fab>
        </Container>
      </Stack>
    </Container>
  );
};

export default NewReceipt;
